import { Signal } from '@angular/core';
import { providePageContext } from '@tourfold/shared-kernels/page-context';
import { FolderBrowserComponent } from '../folder-browser/folder-browser.component';

export interface DocumentManagementPageAiPageContextSignals {
  /** The mounted `<tf-folder-browser>` instance. `undefined` until the page renders. */
  readonly folderBrowser: Signal<FolderBrowserComponent | undefined>;
}

/**
 * Registers AI assistant page context for the global `/app/documents` page.
 *
 * The page hosts a Miller-column folder browser. We surface:
 *  - `entity`: the user's current folder/document selection, with the breadcrumb chain as the
 *    label so the assistant can refer to "the Cases folder" rather than a UUID. **No body / no
 *    file content** — the assistant fetches that via the document-management MCP tools when
 *    needed.
 *  - `filters`: `{ view: <smart-view> }` when the user is in a smart view (recent / starred /
 *    deleted), and `{ breadcrumb: '<path>' }` so the assistant has a stable text handle on the
 *    current location even before the user selects a row.
 *
 * `visible_rows` is left empty intentionally — the column model doesn't have a single "the
 * current list" snapshot the inner browser exposes today. If we surface column listings later
 * we can bring this up to par with the comments page-context.
 *
 * Field semantics: see `frontend/AGENTS.md → "AI Assistant Page Context"`.
 */
export function provideDocumentManagementPageAiPageContext(
  state: DocumentManagementPageAiPageContextSignals,
): void {
  providePageContext(() => {
    const browser = state.folderBrowser();
    if (!browser) {
      // Page mounted but the inner browser hasn't (folder lookup in flight, or load error).
      return { filters: { tab: 'documents' } };
    }
    const sel = browser.currentSelection();
    const crumbs = browser.breadcrumbLabels();
    const smart = browser.activeSmartView();
    const breadcrumb = crumbs.length > 0 ? crumbs.join(' › ') : null;

    const entity = sel
      ? {
          type: sel.kind === 'document' ? 'document' : 'folder',
          id: sel.id,
          label: breadcrumb ?? sel.id,
        }
      : undefined;

    return {
      ...(entity ? { entity } : {}),
      visible_rows: [],
      filters: {
        ...(smart ? { view: smart } : {}),
        ...(breadcrumb ? { breadcrumb } : {}),
      },
    };
  });
}
